import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Key, Shield, Save } from "lucide-react";
import { api, adminAuthHeaders } from "@/lib/api";
import { ADMIN_LOGIN_URL } from "@/lib/adminPath";

const inputCls =
  "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-[#1f3a8a] focus:ring-1 focus:ring-[#1f3a8a] outline-none";
const labelCls = "text-[11px] font-bold text-slate-500 uppercase tracking-wider";

export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("readonly");

  const me = localStorage.getItem("rm_admin_user") || "admin";
  const myRole = localStorage.getItem("rm_admin_role") || "admin";

  const loadUsers = async () => {
    if (myRole !== "admin") { setLoading(false); return; }
    try {
      const { data } = await api.get("/admin/users", { headers: adminAuthHeaders() });
      setUsers(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, []);

  const changeMyPassword = async (e) => {
    e.preventDefault();
    if (nw.length < 6) return toast.error("Nowe hasło min. 6 znaków");
    try {
      await api.post("/admin/change-password", { current_password: cur, new_password: nw }, { headers: adminAuthHeaders() });
      toast.success("Hasło zmienione. Zaloguj się ponownie.");
      setCur(""); setNw("");
      setTimeout(() => {
        localStorage.removeItem("rm_admin_token");
        window.location.assign(ADMIN_LOGIN_URL);
      }, 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd zmiany hasła");
    }
  };

  const addUser = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || newPassword.length < 6) return toast.error("Nazwa i hasło (min. 6 znaków) wymagane");
    try {
      await api.post("/admin/users", { username: newUsername.trim(), password: newPassword, role: newRole }, { headers: adminAuthHeaders() });
      toast.success("Użytkownik dodany");
      setNewUsername(""); setNewPassword(""); setNewRole("readonly");
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd dodawania");
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Usunąć użytkownika ${u.username}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`, { headers: adminAuthHeaders() });
      toast.success("Usunięto");
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd usuwania");
    }
  };

  const resetPass = async (u) => {
    const p = window.prompt(`Nowe hasło dla ${u.username} (min. 6 znaków):`, "");
    if (!p || p.length < 6) return;
    try {
      await api.put(`/admin/users/${u.id}/password`, { new_password: p }, { headers: adminAuthHeaders() });
      toast.success("Hasło zmienione");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Błąd zmiany hasła");
    }
  };

  return (
    <div data-testid="users-panel" className="p-6 space-y-8">
      {/* Zmiana własnego hasła */}
      <section className="rounded-xl border border-slate-200 p-5 bg-white">
        <h3 className="font-bold text-slate-800 flex items-center gap-2"><Key size={18} className="text-[#1f3a8a]" /> Zmień moje hasło</h3>
        <p className="text-xs text-slate-500 mt-1">Zalogowany jako: <strong>{me}</strong> ({myRole})</p>
        <form onSubmit={changeMyPassword} className="grid sm:grid-cols-3 gap-3 mt-4 items-end">
          <div>
            <label className={labelCls}>Obecne hasło</label>
            <input data-testid="my-current-password" type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Nowe hasło (min. 6 znaków)</label>
            <input data-testid="my-new-password" type="password" value={nw} onChange={(e) => setNw(e.target.value)} className={inputCls} />
          </div>
          <button data-testid="save-my-password" type="submit" className="rm-btn-primary inline-flex items-center gap-2 justify-center"><Save size={16} /> Zapisz</button>
        </form>
      </section>

      {myRole !== "admin" ? (
        <div className="rounded-xl border border-slate-200 p-5 bg-slate-50 text-slate-500 text-sm">
          Tylko konto administratora może dodawać i zarządzać użytkownikami.
        </div>
      ) : (
        <>
          {/* Dodawanie użytkownika */}
          <section className="rounded-xl border border-slate-200 p-5 bg-white">
            <h3 className="font-bold text-slate-800 flex items-center gap-2"><UserPlus size={18} className="text-[#1f3a8a]" /> Dodaj nowego użytkownika</h3>
            <form onSubmit={addUser} className="grid sm:grid-cols-4 gap-3 mt-4 items-end">
              <div>
                <label className={labelCls}>Login</label>
                <input data-testid="new-username" value={newUsername} onChange={(e) => setNewUsername(e.target.value)} className={inputCls} placeholder="np. jan.kowalski" />
              </div>
              <div>
                <label className={labelCls}>Hasło</label>
                <input data-testid="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputCls} placeholder="min. 6 znaków" />
              </div>
              <div>
                <label className={labelCls}>Rola</label>
                <select data-testid="new-role" value={newRole} onChange={(e) => setNewRole(e.target.value)} className={inputCls}>
                  <option value="readonly">Tylko odczyt</option>
                  <option value="admin">Administrator (pełne uprawnienia)</option>
                </select>
              </div>
              <button data-testid="add-user-btn" type="submit" className="rm-btn-primary inline-flex items-center gap-2 justify-center"><UserPlus size={16} /> Dodaj</button>
            </form>
          </section>

          {/* Lista użytkowników */}
          <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
            <h3 className="font-bold text-slate-800 flex items-center gap-2 p-5 pb-3"><Shield size={18} className="text-[#1f3a8a]" /> Użytkownicy panelu</h3>
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-slate-100 text-slate-600 uppercase text-xs">
                <tr>
                  <th className="text-left p-3">Login</th>
                  <th className="text-left p-3">Rola</th>
                  <th className="text-left p-3">Utworzony</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={4} className="p-6 text-center text-slate-400">Wczytywanie…</td></tr>}
                {!loading && users.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-400">Brak użytkowników (poza legacy adminem z .env)</td></tr>}
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="p-3 font-semibold text-slate-800">{u.username}</td>
                    <td className="p-3"><span className={`px-2 py-1 rounded-full text-xs font-bold ${u.role === "admin" ? "bg-[#1f3a8a]/10 text-[#1f3a8a]" : "bg-slate-100 text-slate-600"}`}>{u.role === "admin" ? "Administrator" : "Tylko odczyt"}</span></td>
                    <td className="p-3 text-slate-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleString("pl-PL") : "—"}</td>
                    <td className="p-3 text-right whitespace-nowrap">
                      <button title="Zmień hasło" onClick={() => resetPass(u)} className="text-[#1f3a8a] hover:text-[#15296b] mr-3"><Key size={16} /></button>
                      {u.username !== me && (
                        <button title="Usuń" data-testid={`del-user-${u.username}`} onClick={() => removeUser(u)} className="text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </div>
  );
}
